import React, { Component, Fragment } from 'react';
import uniqBy from 'lodash/uniqBy';
import PropTypes from 'prop-types';
import Button from 'react-bootstrap/Button';
import withSounds from 'containers/withSounds';
import ImageFadeComponent from 'components/ImageFadeComponent';
import TextButton from 'components/TextButton';
import { REWARD_TYPE } from 'utils/rewards';
import CloseIcon from 'resources/cancel-circle.svg';
import PurpleW from 'resources/PurpleW.png';
import UberIcon from 'resources/icon-uber.png';
import WStoreGems from '../WStoreGems';
import {
  getAvailableInfo,
  getCategoryItemClassName,
  getCategoryLogo,
  getLocationImage,
  getQuestStockCount,
  getQuestStockInfo,
  getStockInfo,
  getValidityString,
  getWScoreEquivalentOfGemCost,
  isPurchaseEnabled,
  isQuest,
  isQuestActive,
  isUber
} from '../WStoreUtils';
import QuestBanner from '../assets/QuestBanner.png';
import './WStoreItem.css';

const READ_DESCRIPTION_TEXT_LIMIT = 80;
const READ_DESCRIPTION_TEXT_TRIM = READ_DESCRIPTION_TEXT_LIMIT - 15; // Make room for READ MORE text
const BtnWithSounds = withSounds({ mouseEnterSound: true })(Button);

class WStoreItem extends Component {
  constructor(props) {
    super(props);
    this.state = {
      showOverlay: false
    };

    this.defaultImage = this.defaultImage.bind(this);
    this.toggleOverlay = this.toggleOverlay.bind(this);
  }

  toggleOverlay() {
    this.setState({
      showOverlay: !this.state.showOverlay
    });
  }

  splitTitleNameByValidity(itemName) {
    const splitIndex = itemName.indexOf('Valid');
    let title, subTitleValidity;

    if (splitIndex === -1) {
      title = itemName;
      subTitleValidity = null;
    } else {
      title = itemName.substring(0, splitIndex).trim();
      subTitleValidity = itemName.substring(splitIndex).trim();
    }

    return (
      <div className="header-title-container">
        <div className="title">{title}</div>
        {subTitleValidity && (
          <div className="sub-title-validity">{subTitleValidity}</div>
        )}
      </div>
    );
  }

  getOverlayContent(item) {
    const className = `info-overlay ${this.state.showOverlay ? 'in' : 'out'}`;
    return (
      <div className={className} aria-hidden="true">
        <div className="close-overlay">
          <TextButton onClick={this.toggleOverlay}>
            <img src={CloseIcon} alt="Close overlay" />
          </TextButton>
        </div>
        <div className="overlay-content">{item.description}</div>
        <div className="validity-text">{getValidityString(item)}</div>
      </div>
    );
  }

  getUberOverlayContet(item) {
    const className = `info-overlay ${this.state.showOverlay ? 'in' : 'out'}`;

    return (
      <div className={className} aria-hidden="true">
        <div className="close-overlay">
          <TextButton onClick={this.toggleOverlay}>
            <img src={CloseIcon} alt="Close overlay" />
          </TextButton>
        </div>
        <div className="overlay-content">
          <p>{item.description}</p>
          <p>
            You are responsible for any trip charges in excess of the voucher
            value. Vouchers can be redeemed in the Uber app. Instructions for
            voucher use will be included in your confirmation email. Uber
            Voucher terms and conditions apply.
          </p>
          <p>
            Valid [DAY OF WEEK] only at {item.locations[0]}! Not valid online at
            Play.WindCreek.com or any other Wind Creek Property.
          </p>
        </div>
        <div className="validity-text">{getValidityString(item)}</div>
      </div>
    );
  }

  truncateDescriptionText(description, readMore) {
    if (readMore || description.length > READ_DESCRIPTION_TEXT_LIMIT) {
      return (
        <Fragment>
          <span aria-hidden="true">
            {description.substring(0, READ_DESCRIPTION_TEXT_TRIM).trim()}
            {'... '}
            <TextButton className="read-more" onClick={this.toggleOverlay}>
              (READ MORE)
            </TextButton>
          </span>
          <span className="sr-only">{description}</span>
        </Fragment>
      );
    } else {
      return description;
    }
  }

  getCountInfo(item) {
    const limitedAvailable = item.wstoreItemCount.purchaseType !== 'Unlimited';
    const className = `count-view${limitedAvailable ? ' two-column' : ''}`;
    if (isQuest(item)) {
      return <div className="count-view">{getQuestStockInfo(item)}</div>;
    }

    return (
      <div className={className}>
        {getStockInfo(item.wstoreItemCount, true)}
        {getAvailableInfo(item.wstoreItemCount)}
      </div>
    );
  }

  getTradeButton(item) {
    const { onSelectItemWithTrade } = this.props;
    return (
      <BtnWithSounds
        className="redeem-btn"
        variant="green"
        onClick={() => onSelectItemWithTrade(item)}
      >
        TRADE
      </BtnWithSounds>
    );
  }

  getRedeemButton(item, patronAssets, onSelect) {
    if (isQuest(item)) {
      if (isQuestActive(item)) {
        return (
          <BtnWithSounds className="redeem-btn redeem-btn-out" variant="gray">
            IN PROGRESS
          </BtnWithSounds>
        );
      } else if (getQuestStockCount(item) <= 0) {
        return (
          <BtnWithSounds className="redeem-btn redeem-btn-out" variant="gray">
            OUT OF STOCK
          </BtnWithSounds>
        );
      } else {
        return (
          <BtnWithSounds
            className="redeem-btn"
            variant="green"
            onClick={() => onSelect(item)}
          >
            VIEW QUEST
          </BtnWithSounds>
        );
      }
    }

    const buttonLabel = 'Redeem';
    if (isPurchaseEnabled(item, patronAssets)) {
      return (
        <BtnWithSounds
          className="redeem-btn"
          variant="green"
          onClick={() => onSelect(item)}
        >
          {buttonLabel}
        </BtnWithSounds>
      );
    } else if (
      !item.wstoreItemCount.unlimitedInventory &&
      item.wstoreItemCount.availableCount <= 0
    ) {
      return (
        <BtnWithSounds className="redeem-btn redeem-btn-out" variant="gray">
          OUT OF STOCK
        </BtnWithSounds>
      );
    } else {
      return (
        <BtnWithSounds
          className="redeem-btn"
          variant="gray"
          onClick={() => onSelect(item)}
        >
          {buttonLabel}
        </BtnWithSounds>
      );
    }
  }

  defaultImage(e) {
    e.target.onerror = null;
    e.target.src = PurpleW;
  }

  renderValidity(item) {
    if (item.classCode === 'Quest') return null;
    if (item.classCode === REWARD_TYPE.PARTNERS) {
      return (
        <div className="body-validity">{'Valid: ' + item.claimEndDate}</div>
      );
    }
    return <div className="body-validity">{getValidityString(item)}</div>;
  }

  renderDescription(item, readMore) {
    return (
      <div className="body-description">
        {this.truncateDescriptionText(item.description, readMore)}
      </div>
    );
  }

  renderQuestBanner(item) {
    if (isQuest(item)) {
      return <img className="quest-banner" src={QuestBanner} alt="Quest" />;
    }
  }

  renderCategoryLogos(item) {
    if (isQuest(item)) {
      const quest = item.prizeData.questAwardData.quest;
      let questPrizes = quest.prizes.prizes;
      let formattedPrizes = questPrizes.map(prize => ({
        alt: prize.name,
        url: getCategoryLogo(prize.category)
      }));
      formattedPrizes = uniqBy(formattedPrizes, 'url');

      return (
        <ImageFadeComponent
          className="category-icon"
          images={formattedPrizes}
        />
      );
    }

    return (
      <img
        className="category-icon"
        src={getCategoryLogo(item.classCode)}
        alt={item.classCode}
      />
    );
  }

  isTradeAvailable() {
    const { item, patronAssets, exchangeRates } = this.props;

    // Guest's gem balance can cover all the gems required for the item
    const guestHasEnoughNuggets = item.nuggetCost <= patronAssets.nuggets;
    const guestHasEnoughRubies = item.rubyCost <= patronAssets.rubies;
    const guestHasEnoughSapphires = item.sapphireCost <= patronAssets.sapphires;
    const guestHasEnoughWChips = item.wchipCost <= patronAssets.wchips;
    const guestHasEnoughGems =
      guestHasEnoughNuggets &&
      guestHasEnoughRubies &&
      guestHasEnoughSapphires &&
      guestHasEnoughWChips;

    const wscoreEquivalentOfGemCost = getWScoreEquivalentOfGemCost(
      item,
      exchangeRates,
      patronAssets
    );

    return (
      item.exchangeAvailable &&
      !guestHasEnoughGems &&
      wscoreEquivalentOfGemCost > 0 &&
      patronAssets.wscore >= wscoreEquivalentOfGemCost + item.wscore &&
      (item.wstoreItemCount.unlimitedInventory ||
        item.wstoreItemCount.availableCount > 0)
    );
  }

  renderParentElement(children) {
    const { item } = this.props;
    const wstoreItemClass = isQuest(item) ? ' wstore-item-quest' : '';

    return <div className={`wstore-item${wstoreItemClass}`}>{children}</div>;
  }

  render() {
    const { item, onSelect, patronAssets, hideActions } = this.props;
    const bodyClass = `item-body ${getCategoryItemClassName(item.classCode)}`;
    const wstoreItemImage = isQuest(item) ? 'quest-image' : '';
    const showTradeButton = this.isTradeAvailable();

    return this.renderParentElement(
      isUber(item) ? (
        <div className="wstore-item-content">
          <div className={bodyClass}>
            {this.splitTitleNameByValidity(item.name)}
            <div className="body-top">
              <div className="image-container">
                <img
                  className={'item-image'}
                  onError={this.defaultImage}
                  src={item.imageUrl || UberIcon}
                  alt={item.name}
                />
              </div>
              <div className="wscore-details">
                {this.getCountInfo(item)}
                <div className="separator" />
                {this.renderValidity(item)}
              </div>
            </div>

            {this.renderDescription(item, true)}
            {!hideActions && (
              <div className="item-actions">
                <WStoreGems item={item} />
                <div className="button-container">
                  {showTradeButton && this.getTradeButton(item)}
                  {this.getRedeemButton(item, patronAssets, onSelect)}
                </div>
              </div>
            )}
          </div>
          {this.getUberOverlayContet(item)}
        </div>
      ) : (
        <div className="wstore-item-content">
          <div className={bodyClass}>
            {this.splitTitleNameByValidity(item.name)}
            <div className="body-top">
              <div className="image-container">
                <img
                  className={`item-image ${wstoreItemImage}`}
                  onError={this.defaultImage}
                  src={item.imageUrl || PurpleW}
                  alt={item.name}
                />
                {this.renderCategoryLogos(item)}
                {getLocationImage(item)}
              </div>
              <div className="wscore-details">
                {this.getCountInfo(item)}
                <div className="separator" />
                {this.renderValidity(item)}
              </div>
            </div>

            {this.renderDescription(item)}
            {!hideActions && (
              <div className="item-actions">
                <WStoreGems item={item} />
                <div className="button-container">
                  {showTradeButton && this.getTradeButton(item)}
                  {this.getRedeemButton(item, patronAssets, onSelect)}
                </div>
              </div>
            )}
          </div>
          {this.getOverlayContent(item)}
        </div>
      )
    );
  }
}

WStoreItem.propTypes = {
  item: PropTypes.object,
  patronAssets: PropTypes.any,
  onSelect: PropTypes.func,
  onSelectItemWithTrade: PropTypes.func,
  exchangeRates: PropTypes.shape({
    wscorePerNugget: PropTypes.number,
    wscorePerRuby: PropTypes.number,
    wscorePerSapphire: PropTypes.number,
    wscorePerWChip: PropTypes.number
  }),
  isPurchaseEnabled: PropTypes.func,
  isQuest: PropTypes.func,
  isQuestActive: PropTypes.func,
  isUber: PropTypes.func
};

export default WStoreItem;
